<template>
  <div class="users-tab pa-6">
    <v-card class="content-card elevation-0">
      <v-card-title class="pb-4">
        <div>
          <div class="card-title">Orderman Korisnici</div>
          <div class="card-subtitle">Upravljajte Orderman pristupom za korisnike na ovoj lokaciji</div>
        </div>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="users"
          :loading="loading"
          class="orderman-users-table"
          disable-pagination
          hide-default-footer
        >
          <!-- Name column -->
          <template v-slot:[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" :color="getAvatarColor(item.id)" class="mr-3 user-avatar">
                <span class="white--text text-caption font-weight-bold">
                  {{ getInitials(item.name) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text--secondary">{{ item.email }}</div>
              </div>
            </div>
          </template>

          <!-- Orderman Active column -->
          <template v-slot:[`item.orderman_active`]="{ item }">
            <v-switch
              v-model="item.orderman.active"
              :loading="item.updating"
              :disabled="item.updating"
              color="primary"
              hide-details
              class="mt-0 orderman-switch"
              @change="updateOrdermanStatus(item)"
            />
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="text-center py-8">
              <v-icon size="64" color="grey lighten-2">mdi-account-off</v-icon>
              <div class="text-h6 mt-4 text--secondary">Nema korisnika</div>
              <div class="text-caption text--secondary">
                Dodajte korisnike na ovu lokaciju kako biste upravljali Orderman pristupom
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="confirmDialog.show"
      max-width="500"
      persistent
    >
      <v-card class="confirmation-dialog">
        <v-card-title class="dialog-header">
          <v-icon left color="white" size="28">mdi-alert-circle</v-icon>
          <span class="dialog-title">Aktivacija Orderman Usluge</span>
        </v-card-title>

        <v-card-text class="dialog-content pt-4 pb-2">
          <div class="warning-message">
            <v-icon color="warning" size="20" class="mr-2">mdi-information</v-icon>
            <span class="message-text">
              Aktivacijom Orderman usluge za ovog korisnika, usluga će biti <strong>naplaćena prema važećem cjeniku</strong>.
            </span>
          </div>
          <div class="mt-3 confirmation-question">
            Želite li nastaviti s aktivacijom?
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="cancel-button"
            @click="handleConfirmCancel"
          >
            Odustani
          </v-btn>
          <v-btn
            color="primary"
            class="confirm-button"
            @click="handleConfirmOk"
          >
            Aktiviraj
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { df, auth, firebase } from '@/plugins/firebase'
import { recordBilling } from '@/libs/billing'

export default {
  name: 'UsersTab',

  props: {
    locationId: {
      type: String,
      required: true
    },
    companyId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loading: false,
      users: [],
      headers: [
        { text: 'Korisnik', value: 'name', sortable: true },
        { text: 'Orderman Aktivan', value: 'orderman_active', sortable: false, align: 'center', width: '180px' }
      ],
      confirmDialog: {
        show: false,
        resolve: null
      }
    }
  },

  created () {
    this.loadUsers()
  },

  watch: {
    locationId () {
      // Reload users when location changes
      this.loadUsers()
    }
  },

  methods: {
    async loadUsers () {
      this.loading = true

      try {
        // Step 1: Get user references from location
        const locationUsersSnapshot = await df
          .collection('locations')
          .doc(this.locationId)
          .collection('users')
          .get()

        // Step 2: Fetch full user data from users collection
        const userPromises = locationUsersSnapshot.docs.map(async (doc) => {
          const userId = doc.id

          try {
            // Fetch from main users collection
            const userDoc = await df.collection('users').doc(userId).get()

            if (userDoc.exists) {
              const data = userDoc.data()

              // Build name from name/surname
              let name = ''
              if (data.name && data.surname) {
                name = `${data.name} ${data.surname}`.trim()
              } else if (data.name) {
                name = data.name
              } else if (data.surname) {
                name = data.surname
              } else {
                name = userId
              }

              return {
                id: userId,
                name: name,
                email: data.email || '',
                orderman: data.orderman || { active: false },
                updating: false
              }
            } else {
              // Fallback to location user data
              const locationData = doc.data()
              return {
                id: userId,
                name: userId,
                email: locationData.email || '',
                orderman: locationData.orderman || { active: false },
                updating: false
              }
            }
          } catch (error) {
            return null
          }
        })

        const usersData = await Promise.all(userPromises)
        this.users = usersData.filter(u => u !== null)
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju korisnika',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async updateOrdermanStatus (user) {
      // If activating, show confirmation dialog
      if (user.orderman.active) {
        // Show confirmation
        const confirmed = await this.showActivationConfirmation()

        if (!confirmed) {
          // User cancelled, revert the switch
          user.orderman.active = false
          return
        }
      }

      // Set updating state
      this.$set(user, 'updating', true)

      let mainUpdateSuccess = false

      try {
        const userRef = df.collection('users').doc(user.id)
        const isActivating = user.orderman.active

        console.log(`[UsersTab] Starting Orderman ${isActivating ? 'activation' : 'deactivation'} for user ${user.id}`)

        // 1. Update Firestore (glavni update - MORA proći)
        await userRef.update({
          'orderman.active': user.orderman.active
        })

        console.log(`[UsersTab] Firestore update successful`)
        mainUpdateSuccess = true

        // 2. Record billing event (best-effort - ne blokira glavni flow)
        try {
          if (isActivating) {
            console.log(`[UsersTab] Recording billing activation...`)

            // Aktivacija - kreiraj novi zapis
            const billingId = await recordBilling({
              company_id: this.companyId,
              category: 'orderman',
              user_id: auth.currentUser.uid,
              action: 'activate',
              id: user.id,
              custom_data: {
                target_user_id: user.id,
                target_user_email: user.email,
                target_user_name: user.name,
                location_id: this.locationId,
                activated_by: auth.currentUser.email || auth.currentUser.uid
              }
            })

            console.log(`[UsersTab] Billing activation recorded with ID: ${billingId}`)

            // Spremi billing ID u user document za lakše deaktiviranje
            await userRef.update({
              'orderman.billing_id': billingId
            })

            console.log(`[UsersTab] Billing ID saved to user document`)
          } else {
            console.log(`[UsersTab] Recording billing deactivation...`)

            // Deaktivacija - update postojećeg zapisa
            const userDoc = await userRef.get()
            const billingId = userDoc.data()?.orderman?.billing_id

            if (billingId) {
              await recordBilling({
                company_id: this.companyId,
                category: 'orderman',
                user_id: auth.currentUser.uid,
                action: 'deactivate',
                id: user.id,
                custom_data: {
                  deactivated_by: auth.currentUser.email || auth.currentUser.uid,
                  reason: 'manual_deactivation'
                }
              })

              console.log(`[UsersTab] Billing deactivation recorded`)

              // Ukloni billing ID
              await userRef.update({
                'orderman.billing_id': firebase.firestore.FieldValue.delete()
              })

              console.log(`[UsersTab] Billing ID removed from user document`)
            } else {
              console.warn(`[UsersTab] No billing_id found for user ${user.id}, skipping deactivation record`)
            }
          }
        } catch (billingError) {
          // Billing error ne smije zaustaviti glavni flow
          console.error('[UsersTab] Billing operation failed (non-critical):', billingError)
          console.error('[UsersTab] Billing error details:', {
            message: billingError.message,
            code: billingError.code,
            stack: billingError.stack
          })

          // Prikaži upozorenje, ali ne blokiraj glavni update
          this.$root.$emit('show-snackbar', {
            text: 'Upozorenje: Billing zapis nije uspio, ali Orderman status je ažuriran',
            color: 'warning',
            timeout: 6000
          })
        }

        // Success message za glavni update
        this.$root.$emit('show-snackbar', {
          text: user.orderman.active
            ? 'Orderman pristup aktiviran'
            : 'Orderman pristup deaktiviran',
          color: 'success'
        })

        // Verify update by reading back from Firestore
        await this.verifyUserUpdate(user.id)
      } catch (error) {
        // Revert the toggle samo ako glavni update nije uspio
        if (!mainUpdateSuccess) {
          user.orderman.active = !user.orderman.active
        }

        console.error('[UsersTab] Error updating Orderman status:', error)
        console.error('[UsersTab] Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack
        })

        this.$root.$emit('show-snackbar', {
          text: `Greška pri ažuriranju Orderman statusa: ${error.message || 'Nepoznata greška'}`,
          color: 'error'
        })
      } finally {
        this.$set(user, 'updating', false)
      }
    },

    showActivationConfirmation () {
      return new Promise((resolve) => {
        this.confirmDialog = {
          show: true,
          resolve: resolve
        }
      })
    },

    handleConfirmOk () {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(true)
      }
      this.confirmDialog.show = false
    },

    handleConfirmCancel () {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(false)
      }
      this.confirmDialog.show = false
    },

    async verifyUserUpdate (userId) {
      try {
        const userDoc = await df.collection('users').doc(userId).get()
        if (userDoc.exists) {
          const data = userDoc.data()

          // Update local user object with fresh data
          const userIndex = this.users.findIndex(u => u.id === userId)
          if (userIndex !== -1) {
            this.$set(this.users[userIndex], 'orderman', data.orderman || { active: false })
          }
        }
      } catch (error) {
        // Silent fail on verification
      }
    },

    getAvatarColor (userId) {
      // Array of vibrant colors for avatars
      const colors = [
        '#3989C9', // Primary blue
        '#00B3BE', // Teal
        '#6154A4', // Purple
        '#E91E63', // Pink
        '#FF9800', // Orange
        '#4CAF50', // Green
        '#9C27B0', // Deep purple
        '#00BCD4'  // Cyan
      ]

      // Generate consistent color based on user ID
      let hash = 0
      for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash)
      }
      const index = Math.abs(hash) % colors.length
      return colors[index]
    },

    getInitials (name) {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }
  }
}
</script>

<style scoped>
.users-tab {
  min-height: 400px;
}

.content-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.1) !important;
}

.card-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  letter-spacing: 0.3px !important;
}

.card-subtitle {
  font-size: 14px !important;
  color: #6c757d !important;
  margin-top: 4px;
}

.orderman-users-table {
  background: transparent !important;
}

.orderman-users-table >>> .v-data-table__wrapper {
  border-radius: 8px !important;
}

.user-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  font-size: 14px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.user-avatar:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

/* OFF state - gray */
.orderman-switch::v-deep .v-input--switch__track {
  background-color: rgba(230, 230, 230, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(189, 189, 189, 0.5) !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.orderman-switch::v-deep .v-input--switch__thumb {
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.9) !important;
}

/* ON state - green */
.orderman-switch.primary--text::v-deep .v-input--switch__track,
.orderman-switch::v-deep .v-input--is-dirty .v-input--switch__track,
.orderman-switch::v-deep .theme--light.v-input--is-dirty .v-input--switch__track {
  background: linear-gradient(135deg, #81C784 0%, #66BB6A 100%) !important;
  backdrop-filter: blur(12px) !important;
  border-color: rgba(76, 175, 80, 0.8) !important;
  box-shadow: inset 0 2px 6px rgba(76, 175, 80, 0.3), 0 2px 10px rgba(76, 175, 80, 0.3) !important;
}

.orderman-switch.primary--text::v-deep .v-input--switch__thumb,
.orderman-switch::v-deep .v-input--is-dirty .v-input--switch__thumb,
.orderman-switch::v-deep .theme--light.v-input--is-dirty .v-input--switch__thumb {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.6), 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  border-color: rgba(255, 255, 255, 1) !important;
}

/* Confirmation Dialog */
.confirmation-dialog {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(57, 137, 201, 0.2) !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.dialog-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  border-bottom: 1px solid rgba(57, 137, 201, 0.3) !important;
  padding: 20px 24px !important;
}

.dialog-title {
  font-size: 20px !important;
  font-weight: 700 !important;
  color: white !important;
  letter-spacing: 0.3px !important;
}

.dialog-content {
  font-size: 15px !important;
  line-height: 1.6 !important;
  color: #2c3e50 !important;
}

.warning-message {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  background: rgba(255, 152, 0, 0.08) !important;
  border-left: 4px solid #FF9800 !important;
  border-radius: 8px !important;
}

.message-text {
  font-size: 14px !important;
  line-height: 1.5 !important;
  color: #5d4037 !important;
}

.confirmation-question {
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
  text-align: center;
}

.dialog-actions {
  border-top: 1px solid rgba(0, 0, 0, 0.08) !important;
  background: rgba(250, 250, 250, 0.5) !important;
}

.cancel-button {
  color: #757575 !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
}

.cancel-button:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

.confirm-button {
  background: linear-gradient(135deg, #3989C9 0%, #00B3BE 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 3px 8px rgba(57, 137, 201, 0.3) !important;
  padding: 8px 24px !important;
}

.confirm-button:hover {
  box-shadow: 0 5px 12px rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-1px);
}

@media (max-width: 960px) {
  .users-tab {
    padding: 16px;
  }
}
</style>
